import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor, type Content } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Code,
  ImageUp,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import {
  fileTypeIcon,
  formatBytes,
  type PendingAttachment,
} from "@/lib/attachments";
import type { RichDoc } from "@/lib/board";
import { cn } from "@/lib/utils";

/**
 * The one rich-text comment field used everywhere (the changes-requested modal,
 * the comment composer, replies, inline edit). A TipTap editor with a formatting
 * toolbar, drag-and-drop + clipboard-paste of files, and an attachment tray.
 *
 * It's the INPUT only — callers render their own submit affordance via `actions`
 * (a send button, Save/Cancel, or nothing when a surrounding modal footer
 * submits). `onChange` emits both the editor JSON (stored as `rich`) and its
 * flattened text (stored as `body` + used to gate submit). To clear after a
 * successful post, remount via a changing React `key` (content is read on init).
 */
export function RichCommentEditor({
  value,
  onChange,
  onSubmit,
  placeholder,
  compact,
  autoFocus,
  pending,
  onAttach,
  onRemoveAttachment,
  onCancel,
  actions,
}: {
  /** Initial content: a rich doc, plain text (legacy edit), or empty. */
  value: RichDoc | string | null;
  onChange: (doc: RichDoc, text: string) => void;
  onSubmit?: () => void;
  placeholder: string;
  compact?: boolean;
  autoFocus?: boolean;
  pending: PendingAttachment[];
  onAttach: (files: FileList | File[]) => void;
  onRemoveAttachment: (localId: string) => void;
  onCancel?: () => void;
  actions?: ReactNode;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  const editor = useEditor({
    extensions: [
      // Keep the mark/node set to exactly what serialises to ClickUp comment
      // blocks (bold/italic/code/link + lists). Everything else is disabled.
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        link: { openOnClick: false, autolink: true },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: (value ?? "") as Content,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn(
          "overflow-y-auto px-3 py-2 text-sm leading-relaxed focus:outline-none",
          "[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5",
          "[&_a]:text-clickup-purple [&_a]:underline [&_a]:underline-offset-2",
          "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs",
          compact ? "max-h-40 min-h-[2.25rem]" : "max-h-72 min-h-[8rem]",
        ),
      },
      // Paste an image/file from the clipboard → attach it, don't paste as text.
      handlePaste: (_view, event) => {
        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          onAttach(files);
          return true;
        }
        return false;
      },
      // Stop ProseMirror trying to handle a file drop; the wrapper's onDrop adds
      // it (the native event still bubbles to the wrapper after this).
      handleDrop: (_view, event) => {
        const dt = (event as DragEvent).dataTransfer;
        return !!(dt?.files && dt.files.length > 0);
      },
      handleKeyDown: (_view, event) => {
        if (event.key === "Escape" && onCancel) {
          onCancel();
          return true;
        }
        if (event.key === "Enter" && onSubmit) {
          const withMod = event.metaKey || event.ctrlKey;
          // Compact composer: Enter sends, Shift+Enter is a newline. The roomy
          // modal: Enter is a newline, ⌘/Ctrl+Enter sends (so feedback can wrap).
          if (compact ? !event.shiftKey : withMod) {
            event.preventDefault();
            onSubmit();
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getJSON() as RichDoc, e.getText()),
  });

  function hasFiles(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer?.types ?? []).includes("Files");
  }

  return (
    <div
      className="relative rounded-lg border border-border bg-background focus-within:ring-1 focus-within:ring-ring"
      onDragEnter={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (hasFiles(e)) e.preventDefault();
      }}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
          dragDepth.current = 0;
          setDragOver(false);
        }
      }}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        onAttach(e.dataTransfer.files);
      }}
    >
      {/* Formatting toolbar */}
      <div className="flex items-center gap-0.5 border-b border-border/70 px-1.5 py-1">
        <ToolbarButton
          label="Bold"
          active={editor?.isActive("bold")}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor?.isActive("italic")}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Code"
          active={editor?.isActive("code")}
          onClick={() => editor?.chain().focus().toggleCode().run()}
        >
          <Code className="size-4" />
        </ToolbarButton>
        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
        <ToolbarButton
          label="Bulleted list"
          active={editor?.isActive("bulletList")}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor?.isActive("orderedList")}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Link"
          active={editor?.isActive("link")}
          onClick={() => setLink(editor)}
        >
          <Link2 className="size-4" />
        </ToolbarButton>
        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
        <ToolbarButton label="Attach file" onClick={() => fileInput.current?.click()}>
          <Paperclip className="size-4" />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />

      {pending.length > 0 && (
        <AttachmentTray pending={pending} onRemove={onRemoveAttachment} />
      )}

      {actions && (
        <div className="flex items-center justify-end gap-1 px-1.5 pb-1.5">
          {actions}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) onAttach(e.target.files);
          e.target.value = ""; // allow re-selecting the same file
        }}
      />

      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-clickup-purple bg-background/90 text-sm font-medium text-clickup-purple">
          <ImageUp className="size-5" />
          Drop files to attach
        </div>
      )}
    </div>
  );
}

function ToolbarButton({
  children,
  label,
  active,
  onClick,
}: {
  children: ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection on click
      onClick={onClick}
      className={cn(
        "flex size-7 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        active && "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function AttachmentTray({
  pending,
  onRemove,
}: {
  pending: PendingAttachment[];
  onRemove: (localId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 px-3 pb-2 pt-1">
      {pending.map((p) => {
        const isImage = p.file.type.startsWith("image/");
        const Icon = fileTypeIcon(p.file.type);
        return (
          <div
            key={p.localId}
            className={cn(
              "group relative flex items-center gap-2 rounded-md border border-border bg-muted/40 p-1.5 pr-7 text-xs",
              p.status === "error" && "border-signoff-red/50 bg-signoff-red/5",
            )}
            title={p.error ?? p.file.name}
          >
            {isImage && p.previewUrl ? (
              <img
                src={p.previewUrl}
                alt={p.file.name}
                className="size-9 rounded object-cover"
              />
            ) : (
              <span className="flex size-9 items-center justify-center rounded bg-muted text-muted-foreground">
                <Icon className="size-4" />
              </span>
            )}
            <div className="flex min-w-0 max-w-[10rem] flex-col">
              <span className="truncate font-medium text-foreground">
                {p.file.name}
              </span>
              <span className="text-muted-foreground">
                {p.status === "error"
                  ? (p.error ?? "Failed")
                  : formatBytes(p.file.size)}
              </span>
            </div>
            {p.status === "uploading" && (
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            )}
            <button
              type="button"
              aria-label={`Remove ${p.file.name}`}
              onClick={() => onRemove(p.localId)}
              className="absolute right-1 top-1 flex size-5 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Toolbar link action — prompt for a URL, toggle the link mark on the selection. */
function setLink(editor: ReturnType<typeof useEditor>): void {
  if (!editor) return;
  const prev = editor.getAttributes("link").href as string | undefined;
  const url = window.prompt("Link URL", prev ?? "https://");
  if (url === null) return; // cancelled
  if (url.trim() === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({ href: url.trim() })
    .run();
}
